import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { http } from '@/lib/api';
import {
  ArrowLeft, ArrowRight, MessageCircleHeart, Phone, CheckCircle2, HandHeart, HelpCircle,
  MessageSquare, Ban, Lightbulb, Users as UsersIcon, ShieldAlert, Sparkles,
} from 'lucide-react';

const Card = ({ icon: Icon, title, accent='white', children, testid }) => {
  const bg = accent==='navy' ? 'bg-navy text-white' : accent==='beige' ? 'bg-beige' : accent==='red' ? 'bg-red-50 border-2 border-red-200' : 'bg-white';
  const iconColor = accent==='navy' ? 'text-gold' : accent==='red' ? 'text-red-600' : 'text-navy/60';
  return (
    <div data-testid={testid} className={`rounded-3xl p-6 lg:p-10 card-soft ${bg}`}>
      {(Icon || title) && (
        <div className="flex items-center gap-3 mb-4">
          {Icon && <Icon size={20} strokeWidth={1.5} className={iconColor}/>}
          {title && <p className="font-heading text-lg lg:text-2xl">{title}</p>}
        </div>
      )}
      {children}
    </div>
  );
};

const BulletList = ({ items, dot='gold' }) => (
  <ul className="space-y-3">
    {items.map((s,i) => (
      <li key={`${i}-${s}`} className="flex items-start gap-3 text-sm lg:text-base leading-relaxed">
        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${dot==='red'?'bg-red-500':dot==='green'?'bg-green':'bg-gold'}`}/>
        <span>{s}</span>
      </li>
    ))}
  </ul>
);

function buildSteps(guide) {
  return [
    { key: 'welcome', title: 'Introduction' },
    { key: 'understanding', title: 'Understanding the issue' },
    { key: 'signs', title: 'Signs to watch for' },
    { key: 'what_helps', title: 'What helps' },
    { key: 'what_doesnt', title: 'What doesn\'t help' },
    { key: 'reflection', title: 'Reflection' },
    { key: 'action', title: 'Action checklist' },
    { key: 'urgent', title: 'When immediate help is needed' },
    { key: 'complete', title: 'You did the work' },
  ];
}

export default function SupportGuide() {
  const { guideId } = useParams();
  const nav = useNavigate();
  const [guide, setGuide] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    http.get(`/support/guides/${guideId}`)
      .then(r => setGuide(r.data))
      .catch(() => setNotFound(true));
  }, [guideId]);

  const steps = useMemo(() => guide ? buildSteps(guide) : [], [guide]);

  if (notFound) return (
    <div className="p-10 text-center">
      <p className="font-heading text-2xl">Guide not found</p>
      <Link to="/app/support" className="inline-flex items-center gap-2 mt-4 text-navy underline"><ArrowLeft size={14}/> Back to Support</Link>
    </div>
  );
  if (!guide) return <div className="p-10 text-navy/60">Loading…</div>;

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const progress = ((step + 1) / steps.length) * 100;

  const goNext = () => {
    if (isLast) { nav('/app/support'); return; }
    setDirection(1); setStep(step + 1);
  };
  const goBack = () => {
    if (step === 0) { nav('/app/support'); return; }
    setDirection(-1); setStep(step - 1);
  };

  const toggle = (i) => setChecked(prev => ({ ...prev, [i]: !prev[i] }));

  const renderStep = () => {
    switch (current.key) {
      case 'welcome':
        return (
          <div className="space-y-5 lg:space-y-6">
            <Card accent="navy" testid="step-welcome">
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Support Guide</p>
              <h1 className="font-heading text-3xl lg:text-5xl mt-2 leading-tight">{guide.title}</h1>
              <p className="text-white/75 text-base lg:text-lg mt-4">{guide.subtitle}</p>
              <p className="text-white/55 text-xs lg:text-sm mt-4 uppercase tracking-widest">{guide.audience}</p>
            </Card>
            <Card icon={Lightbulb} title="Why this matters" accent="beige" testid="step-why">
              <p className="text-sm lg:text-lg leading-relaxed">{guide.why_it_matters}</p>
            </Card>
            <p className="text-sm text-navy/60 text-center">Estimated 4–6 min · 9 steps</p>
          </div>
        );

      case 'understanding':
        return (
          <Card icon={HelpCircle} title="Understanding the issue" testid="step-understanding">
            <p className="text-base lg:text-lg leading-relaxed">{guide.what_it_looks_like}</p>
          </Card>
        );

      case 'signs':
        return (
          <Card icon={HelpCircle} title="Signs to watch for — check what you've noticed" testid="step-signs">
            <ul className="space-y-3">
              {(guide.signs || []).map((s,i) => (
                <li key={`${i}-${s}`}>
                  <button onClick={()=>toggle(`sign-${i}`)} data-testid={`sign-check-${i}`}
                    className={`w-full text-left p-4 rounded-2xl transition-smooth flex items-start gap-3 ${checked[`sign-${i}`] ? 'bg-navy text-white' : 'bg-beige hover:bg-[#EAE5D9]'}`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${checked[`sign-${i}`] ? 'bg-white text-navy' : 'bg-white border border-navy/20'}`}>
                      {checked[`sign-${i}`] && <CheckCircle2 size={14}/>}
                    </span>
                    <span className="text-sm lg:text-base leading-relaxed">{s}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        );

      case 'what_helps':
        return (
          <div className="space-y-5">
            <Card icon={HandHeart} title="What helps — how to respond" accent="beige" testid="step-what-helps">
              <BulletList items={guide.how_to_respond || []}/>
            </Card>
            <Card icon={MessageSquare} title="Conversation starters — what to say" testid="step-what-to-say">
              <BulletList items={guide.what_to_say || []} dot="green"/>
              {guide.conversation_starter && (
                <div className="mt-6 bg-navy text-white rounded-2xl p-5 lg:p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-2">Try this opener</p>
                  <p className="font-heading text-base lg:text-xl leading-relaxed">&quot;{guide.conversation_starter}&quot;</p>
                </div>
              )}
            </Card>
          </div>
        );

      case 'what_doesnt':
        return (
          <Card icon={Ban} title="What doesn't help — try this instead" testid="step-what-doesnt">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
              {(guide.what_not_to_say || []).map((s,i) => {
                const alt = (guide.what_to_say || [])[i] || '(offer presence without words)';
                return (
                  <div key={`${i}-${s}`} className="bg-white rounded-2xl p-5 border-2 border-navy/5">
                    <p className="text-xs uppercase tracking-widest text-red-500 font-semibold mb-1">Instead of…</p>
                    <p className="text-sm lg:text-base text-navy/70 line-through">{s}</p>
                    <p className="text-xs uppercase tracking-widest text-green font-semibold mb-1 mt-4">Try…</p>
                    <p className="text-sm lg:text-base text-navy">{alt}</p>
                  </div>
                );
              })}
            </div>
          </Card>
        );

      case 'reflection':
        return (
          <Card icon={Lightbulb} title="Reflection" accent="beige" testid="step-reflection">
            <p className="text-base lg:text-xl italic leading-relaxed">&quot;{guide.reflection}&quot;</p>
            <p className="text-xs text-navy/50 mt-4">Take a beat. Journal the answer in your Journal if it helps.</p>
          </Card>
        );

      case 'action':
        return (
          <Card icon={CheckCircle2} title="Action checklist — mark what you'll do" testid="step-action">
            <ul className="space-y-3">
              {(guide.action_checklist || []).map((s,i) => (
                <li key={`${i}-${s}`}>
                  <button onClick={()=>toggle(`act-${i}`)} data-testid={`action-check-${i}`}
                    className={`w-full text-left p-4 rounded-2xl transition-smooth flex items-start gap-3 ${checked[`act-${i}`] ? 'bg-green/15' : 'bg-beige hover:bg-[#EAE5D9]'}`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${checked[`act-${i}`] ? 'bg-green text-white' : 'bg-white border border-navy/20'}`}>
                      {checked[`act-${i}`] && <CheckCircle2 size={14}/>}
                    </span>
                    <span className="text-sm lg:text-base leading-relaxed">{s}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        );

      case 'urgent':
        return (
          <div className="space-y-5">
            <Card icon={UsersIcon} title="When to encourage therapy" accent="beige" testid="step-therapy">
              <p className="text-sm lg:text-lg leading-relaxed">{guide.when_to_encourage_therapy}</p>
            </Card>
            <Card icon={Phone} title="When immediate help is needed" accent="red" testid="step-urgent-help">
              <p className="text-sm lg:text-lg leading-relaxed text-red-950">{guide.when_to_seek_urgent_help}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <a href="tel:988" className="inline-flex items-center gap-1 bg-red-600 text-white rounded-full px-4 py-2 text-sm hover:bg-red-700"><Phone size={12}/> Call 988</a>
                <a href="sms:988" className="inline-flex items-center gap-1 bg-red-600 text-white rounded-full px-4 py-2 text-sm hover:bg-red-700"><MessageSquare size={12}/> Text 988</a>
                <a href="tel:911" className="inline-flex items-center gap-1 bg-red-600 text-white rounded-full px-4 py-2 text-sm hover:bg-red-700"><Phone size={12}/> Call 911</a>
              </div>
            </Card>
          </div>
        );

      case 'complete':
        return (
          <div className="space-y-6">
            <Card accent="navy" testid="step-complete">
              <Sparkles size={28} className="text-gold mb-3"/>
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Guide complete</p>
              <h2 className="font-heading text-3xl lg:text-5xl mt-2">Congratulations.</h2>
              <p className="text-white/75 text-base lg:text-lg mt-3 lg:leading-relaxed">
                You just did something most people never do — you learned how to show up better. Small, consistent presence changes lives.
              </p>
            </Card>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4">
              <Link to="/app/support" className="block bg-white rounded-3xl p-6 card-soft hover:-translate-y-0.5 transition-smooth">
                <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold">Continue</p>
                <p className="font-heading text-lg mt-2">More Support guides</p>
              </Link>
              <Link to="/app/support/coach" className="block bg-white rounded-3xl p-6 card-soft hover:-translate-y-0.5 transition-smooth">
                <p className="text-xs uppercase tracking-widest text-gold font-semibold">Talk it through</p>
                <p className="font-heading text-lg mt-2 flex items-center gap-2"><MessageCircleHeart size={16}/> Support Coach</p>
              </Link>
              <Link to="/app/learn" className="block bg-white rounded-3xl p-6 card-soft hover:-translate-y-0.5 transition-smooth">
                <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold">Learn more</p>
                <p className="font-heading text-lg mt-2">Explore Brotherhood University</p>
              </Link>
            </div>
          </div>
        );

      default: return null;
    }
  };

  return (
    <div className="p-6 lg:px-10 lg:pt-10 pb-32 lg:max-w-4xl lg:mx-auto" data-testid={`guide-${guide.id}`}>
      <button onClick={goBack} data-testid="guide-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth mb-4">
        <ArrowLeft size={14}/> {step === 0 ? 'Back to Support' : 'Previous step'}
      </button>

      {/* Progress bar + step indicator */}
      <div className="mb-4 lg:mb-6 flex items-center justify-between text-xs lg:text-sm text-navy/60">
        <span>Support Guide · {guide.title}</span>
        <span data-testid="guide-step-indicator">Step {step + 1} of {steps.length}</span>
      </div>
      <div className="h-1 bg-navy/10 rounded-full overflow-hidden mb-8 lg:mb-10">
        <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} data-testid="guide-progress-bar"/>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={step}
          initial={{ opacity: 0, x: direction * 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -40 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
          {renderStep()}
        </motion.div>
      </AnimatePresence>

      {/* Sticky Prev/Next bar */}
      <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 bg-[#F9F8F6]/95 backdrop-blur border-t border-navy/5 px-6 py-4 lg:py-5">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <button onClick={goBack} data-testid="guide-prev"
            className="inline-flex items-center gap-2 text-sm lg:text-base text-navy/60 hover:text-navy">
            <ArrowLeft size={14}/> Previous
          </button>
          <button onClick={goNext} data-testid="guide-next"
            className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-6 py-3 lg:px-8 lg:py-4 hover:bg-navy-soft transition-smooth text-sm lg:text-base">
            {isLast ? 'Return to Support' : 'Next'} <ArrowRight size={16}/>
          </button>
        </div>
      </div>
    </div>
  );
}
